import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, Typography } from '@mui/material';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';

import { NAV_BG, AMBER, AMBER_DARK } from '../theme';
const DISMISS_KEY = 'sb_push_prompt_dismissed';
const IOS_DISMISS_KEY = 'sb_ios_notif_prompt_dismissed';

const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export default function PushPromptBanner() {
  const { subscribeToPush, getPushStatus } = useContext(AuthContext);
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [iosInstallMode, setIosInstallMode] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY);
    getPushStatus().then(status => {
      if (status === 'not_asked' && !dismissed) {
        setVisible(true);
      } else if (status === 'not_subscribed') {
        // Subscription was lost (expired or cleared) — always re-prompt regardless of dismiss
        setVisible(true);
      } else if (status === 'unsupported' && isIOS() && !isStandalone()) {
        // iOS Safari without PWA install — notifications require Home Screen app
        if (!localStorage.getItem(IOS_DISMISS_KEY)) {
          setIosInstallMode(true);
          setVisible(true);
        }
      }
    });
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    setWorking(true);
    const result = await subscribeToPush();
    setWorking(false);
    // Hide banner regardless — either it worked, or the user said no at the OS prompt
    setVisible(false);
    if (result === 'denied') {
      // They blocked it at the browser prompt — don't keep asking
      localStorage.setItem(DISMISS_KEY, '1');
    }
  };

  const handleDismiss = () => {
    if (iosInstallMode) {
      localStorage.setItem(IOS_DISMISS_KEY, '1');
    } else {
      localStorage.setItem(DISMISS_KEY, '1');
    }
    setVisible(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        background: `linear-gradient(90deg, ${NAV_BG} 0%, #163571 100%)`,
        borderBottom: `2px solid ${AMBER}`,
      }}
    >
      {iosInstallMode
        ? <IosShareIcon sx={{ color: AMBER, fontSize: 20, flexShrink: 0 }} />
        : <NotificationsActiveIcon sx={{ color: AMBER, fontSize: 20, flexShrink: 0 }} />
      }
      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#e2e8f0', flex: 1 }}>
        {iosInstallMode ? t('push_prompt.ios_install') : t('push_prompt.message')}
      </Typography>
      {!iosInstallMode && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={working}
          onClick={handleEnable}
          sx={{
            background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`,
            color: NAV_BG,
            fontFamily: '"Outfit", sans-serif',
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 1.5,
            fontSize: '0.8rem',
            px: 1.5,
            py: 0.4,
            flexShrink: 0,
          }}
        >
          {t('push_prompt.enable')}
        </Button>
      )}
      <IconButton size="small" onClick={handleDismiss} sx={{ color: '#94a3b8', p: 0.5, flexShrink: 0 }}>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
